import type { Lang } from '../types';

export interface CommonText {
  save: string;
  saving: string;
  cancel: string;
  delete: string;
  deleting: string;
  loading: string;
  error: string;
  confirm: string;
  yes: string;
  no: string;
  active: string;
  inactive: string;
  activate: string;
  deactivate: string;
  edit: string;
  add: string;
  close: string;
  back: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  note: string;
  actions: string;
  status: string;
  noResults: string;
  tryAgain: string;
  unknownError: string;
  signOut: string;
  appLanguage: string;
  // Navigation
  navHome: string;
  navLogin: string;
  navRegister: string;
  navApartments: string;
  navCueAddApartment: string;
  navCueConnectCalendar: string;
  navReservations: string;
  navCalendar: string;
  navSettings: string;
  navDashboard: string;
  navUsers: string;
  navBilling: string;
  navUbyport: string;
  navEmail: string;
  navCityTaxMailboxes: string;
  navScheduledJobs: string;
  navApiUsage: string;
  navLogout: string;
  // Filter UI
  showFilters: string;
  hideFilters: string;
  search: string;
  clearFilters: string;
  // Suspension banner
  suspendedMsg: string;
  suspendedViewSubscription: string;
}

export const common: Record<Lang, CommonText> = {
  cs: {
    save: 'Uložit',
    saving: 'Ukládám…',
    cancel: 'Zrušit',
    delete: 'Smazat',
    deleting: 'Mažu…',
    loading: 'Načítám…',
    error: 'Chyba',
    confirm: 'Potvrdit',
    yes: 'Ano',
    no: 'Ne',
    active: 'Aktivní',
    inactive: 'Neaktivní',
    activate: 'Aktivovat',
    deactivate: 'Deaktivovat',
    edit: 'Upravit',
    add: 'Přidat',
    close: 'Zavřít',
    back: 'Zpět',
    name: 'Název',
    email: 'E-mail',
    phone: 'Telefon',
    address: 'Adresa',
    note: 'Poznámka',
    actions: 'Akce',
    status: 'Stav',
    noResults: 'Žádné výsledky.',
    tryAgain: 'Zkusit znovu',
    unknownError: 'Nastala neznámá chyba.',
    signOut: 'Odhlásit se',
    appLanguage: 'Jazyk aplikace',
    navHome: 'Domů',
    navLogin: 'Přihlásit se',
    navRegister: 'Registrovat se',
    navApartments: 'Apartmány',
    navCueAddApartment: 'Další krok: přidejte první apartmán',
    navCueConnectCalendar: 'Další krok: připojte kalendář nebo channel manager',
    navReservations: 'Rezervace',
    navCalendar: 'Kalendář',
    navSettings: 'Nastavení',
    navDashboard: 'Přehled',
    navUsers: 'Uživatelé',
    navBilling: 'Fakturace',
    navUbyport: 'Ubyport',
    navEmail: 'E-mail',
    navCityTaxMailboxes: 'Datové schránky',
    navScheduledJobs: 'Naplánované úlohy',
    navApiUsage: 'Využití API',
    navLogout: 'Odhlásit se',
    showFilters: 'Zobrazit filtry',
    hideFilters: 'Skrýt filtry',
    search: 'Hledat',
    clearFilters: 'Vymazat filtry',
    suspendedMsg: 'Váš účet je pozastaven. Vytváření rezervací, přidávání bytů a další funkce jsou dočasně zakázány.',
    suspendedViewSubscription: 'Zobrazit předplatné →',
  },
  en: {
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    delete: 'Delete',
    deleting: 'Deleting…',
    loading: 'Loading…',
    error: 'Error',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    inactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    back: 'Back',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    note: 'Note',
    actions: 'Actions',
    status: 'Status',
    noResults: 'No results.',
    tryAgain: 'Try again',
    unknownError: 'An unknown error occurred.',
    signOut: 'Sign out',
    appLanguage: 'App language',
    navHome: 'Home',
    navLogin: 'Login',
    navRegister: 'Register',
    navApartments: 'Apartments',
    navCueAddApartment: 'Next step: add your first apartment',
    navCueConnectCalendar: 'Next step: connect a calendar or channel manager',
    navReservations: 'Reservations',
    navCalendar: 'Calendar',
    navSettings: 'Settings',
    navDashboard: 'Dashboard',
    navUsers: 'Users',
    navBilling: 'Billing',
    navUbyport: 'Ubyport',
    navEmail: 'Email',
    navCityTaxMailboxes: 'Mailboxes',
    navScheduledJobs: 'Scheduled Jobs',
    navApiUsage: 'API Usage',
    navLogout: 'Logout',
    showFilters: 'Show Filters',
    hideFilters: 'Hide Filters',
    search: 'Search',
    clearFilters: 'Clear Filters',
    suspendedMsg: 'Your account is suspended. Creating reservations, adding apartments and other features are temporarily disabled.',
    suspendedViewSubscription: 'View subscription →',
  },
  vi: {
    save: 'Lưu',
    saving: 'Đang lưu…',
    cancel: 'Hủy',
    delete: 'Xóa',
    deleting: 'Đang xóa…',
    loading: 'Đang tải…',
    error: 'Lỗi',
    confirm: 'Xác nhận',
    yes: 'Có',
    no: 'Không',
    active: 'Hoạt động',
    inactive: 'Không hoạt động',
    activate: 'Kích hoạt',
    deactivate: 'Vô hiệu hóa',
    edit: 'Chỉnh sửa',
    add: 'Thêm',
    close: 'Đóng',
    back: 'Quay lại',
    name: 'Tên',
    email: 'Email',
    phone: 'Điện thoại',
    address: 'Địa chỉ',
    note: 'Ghi chú',
    actions: 'Thao tác',
    status: 'Trạng thái',
    noResults: 'Không có kết quả.',
    tryAgain: 'Thử lại',
    unknownError: 'Đã xảy ra lỗi không xác định.',
    signOut: 'Đăng xuất',
    appLanguage: 'Ngôn ngữ ứng dụng',
    navHome: 'Trang chủ',
    navLogin: 'Đăng nhập',
    navRegister: 'Đăng ký',
    navApartments: 'Căn hộ',
    navCueAddApartment: 'Bước tiếp theo: thêm căn hộ đầu tiên',
    navCueConnectCalendar: 'Bước tiếp theo: kết nối lịch hoặc channel manager',
    navReservations: 'Lịch đặt',
    navCalendar: 'Lịch',
    navSettings: 'Cài đặt',
    navDashboard: 'Bảng điều khiển',
    navUsers: 'Người dùng',
    navBilling: 'Thanh toán',
    navUbyport: 'Ubyport',
    navEmail: 'Email',
    navCityTaxMailboxes: 'Hộp thư',
    navScheduledJobs: 'Tác vụ theo lịch',
    navApiUsage: 'Sử dụng API',
    navLogout: 'Đăng xuất',
    showFilters: 'Hiện bộ lọc',
    hideFilters: 'Ẩn bộ lọc',
    search: 'Tìm kiếm',
    clearFilters: 'Xóa bộ lọc',
    suspendedMsg: 'Tài khoản của bạn đã bị tạm ngưng. Tạo lịch đặt, thêm căn hộ và các tính năng khác tạm thời bị vô hiệu hóa.',
    suspendedViewSubscription: 'Xem đăng ký dịch vụ →',
  },
  uk: {
    save: 'Зберегти',
    saving: 'Збереження…',
    cancel: 'Скасувати',
    delete: 'Видалити',
    deleting: 'Видалення…',
    loading: 'Завантаження…',
    error: 'Помилка',
    confirm: 'Підтвердити',
    yes: 'Так',
    no: 'Ні',
    active: 'Активний',
    inactive: 'Неактивний',
    activate: 'Активувати',
    deactivate: 'Деактивувати',
    edit: 'Редагувати',
    add: 'Додати',
    close: 'Закрити',
    back: 'Назад',
    name: 'Назва',
    email: 'Ел. пошта',
    phone: 'Телефон',
    address: 'Адреса',
    note: 'Примітка',
    actions: 'Дії',
    status: 'Статус',
    noResults: 'Немає результатів.',
    tryAgain: 'Спробувати знову',
    unknownError: 'Сталася невідома помилка.',
    signOut: 'Вийти',
    appLanguage: 'Мова додатку',
    navHome: 'Головна',
    navLogin: 'Увійти',
    navRegister: 'Реєстрація',
    navApartments: 'Апартаменти',
    navCueAddApartment: 'Наступний крок: додайте перший апартамент',
    navCueConnectCalendar: 'Наступний крок: підключіть календар або channel manager',
    navReservations: 'Бронювання',
    navCalendar: 'Календар',
    navSettings: 'Налаштування',
    navDashboard: 'Панель',
    navUsers: 'Користувачі',
    navBilling: 'Білінг',
    navUbyport: 'Ubyport',
    navEmail: 'Email',
    navCityTaxMailboxes: 'Поштові скриньки',
    navScheduledJobs: 'Заплановані завдання',
    navApiUsage: 'Використання API',
    navLogout: 'Вийти',
    showFilters: 'Показати фільтри',
    hideFilters: 'Сховати фільтри',
    search: 'Пошук',
    clearFilters: 'Очистити фільтри',
    suspendedMsg: 'Ваш обліковий запис призупинено. Створення бронювань, додавання апартаментів та інші функції тимчасово недоступні.',
    suspendedViewSubscription: 'Переглянути підписку →',
  },
};
